import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import type {
  ApplyReport,
  Board,
  BoardStatusFilter,
  FamilyReenrollment,
  PlacementDecision,
  ReenrollmentSettings,
  RequestStatus,
  SeatSummary,
} from './types'

export interface BoardFilters {
  toYearId: number
  fromYearId?: number | null
  classId?: number | null
  sectionId?: number | null
  examId?: number | null
  status?: BoardStatusFilter | null
  requestStatus?: RequestStatus | null
}

export const promotionKeys = {
  all: ['promotion'] as const,
  board: (f: BoardFilters) => [...promotionKeys.all, 'board', f] as const,
  capacity: (toYearId: number) => [...promotionKeys.all, 'capacity', toYearId] as const,
  settings: (toYearId: number) => [...promotionKeys.all, 'settings', toYearId] as const,
  family: (studentId: number) => [...promotionKeys.all, 'family', studentId] as const,
}

export const boardQuery = (f: BoardFilters) =>
  queryOptions({
    queryKey: promotionKeys.board(f),
    queryFn: () => api.get<Board>('/next-year/placements', { params: clean(f) }),
  })
export const capacityQuery = (toYearId: number) =>
  queryOptions({
    queryKey: promotionKeys.capacity(toYearId),
    queryFn: () => api.get<SeatSummary[]>('/next-year/capacity', { params: { toYearId } }),
  })
export const settingsQuery = (toYearId: number) =>
  queryOptions({
    queryKey: promotionKeys.settings(toYearId),
    queryFn: () => api.get<ReenrollmentSettings>('/next-year/settings', { params: { toYearId } }),
  })
export const familyReenrollmentQuery = (studentId: number) =>
  queryOptions({
    queryKey: promotionKeys.family(studentId),
    queryFn: () => api.get<FamilyReenrollment>(`/me/family/students/${studentId}/reenrollment`),
  })

function clean(f: object): Record<string, string | number> {
  const out: Record<string, string | number> = {}
  for (const [k, v] of Object.entries(f))
    if (v !== null && v !== undefined && v !== '') out[k] = v as string | number
  return out
}

export interface PlacementRowInput {
  studentId: number
  decision: PlacementDecision
  targetClassId: number | null
  targetSectionId: number | null
  note: string | null
}

function usePromotionMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => void qc.invalidateQueries({ queryKey: promotionKeys.all }),
  })
}

export const useSavePlacements = () =>
  usePromotionMutation((body: { fromYearId?: number; toYearId: number; rows: PlacementRowInput[] }) =>
    api.put<{ saved: number }>('/next-year/placements', body),
  )
export const useUpdatePlacement = () =>
  usePromotionMutation(({ id, ...body }: Omit<PlacementRowInput, 'studentId'> & { id: number }) =>
    api.patch<unknown>(`/next-year/placements/${id}`, body),
  )
export const useConfirmPlacements = () =>
  usePromotionMutation((ids: number[]) =>
    api.post<{ confirmed: number }>('/next-year/placements/confirm', { ids }),
  )
export const useUnconfirmPlacements = () =>
  usePromotionMutation((ids: number[]) =>
    api.post<{ updated: number }>('/next-year/placements/unconfirm', { ids }),
  )
export const useCancelPlacements = () =>
  usePromotionMutation((ids: number[]) =>
    api.post<{ cancelled: number }>('/next-year/placements/cancel', { ids }),
  )
export const useApplyPlacements = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (toYearId: number) => api.post<ApplyReport>('/next-year/apply', { toYearId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: promotionKeys.all })
      void qc.invalidateQueries({ queryKey: ['students'] })
      void qc.invalidateQueries({ queryKey: ['academic'] })
    },
  })
}
export const useSaveReenrollmentSettings = () =>
  usePromotionMutation(
    (body: { toYearId: number; isOpen: boolean; deadline: string | null; message: string | null }) =>
      api.put<ReenrollmentSettings>('/next-year/settings', body),
  )

export const useAnswerReenrollment = (studentId: number) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { returning: boolean; requestedClassId: number | null; note: string | null }) =>
      api.post<FamilyReenrollment>(`/me/family/students/${studentId}/reenrollment`, body),
    onSuccess: (data) => qc.setQueryData(promotionKeys.family(studentId), data),
  })
}
